import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { DataTable } from "../shared/DataTable";
import { Pagination } from "../shared/Pagination";
import { StatusBadge } from "../shared/StatusBadge";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { Input } from "../ui/input";
import { AddonFormDialog } from "./AddonFormDialog";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { itemStatusMeta } from "../../lib/statusMaps";
import { formatCurrency } from "../../lib/utils";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useListState } from "../../hooks/useListState";

const LIMIT = 20;

export function AddonsTab() {
  const [state, setState] = useState({ loading: true, rows: [], total: 0 });
  const [page, setPage] = useListState("catalog.addons.page", 0);
  const [search, setSearch] = useListState("catalog.addons.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [dialogAddon, setDialogAddon] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    apiClient.get("/addons", { params: { q: debouncedSearch || undefined, limit: LIMIT, offset: page * LIMIT } })
      .then(({ data }) => setState({ loading: false, rows: data.items, total: data.total }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load addons.")); setState((s) => ({ ...s, loading: false })); });
  };

  useEffect(refresh, [page, debouncedSearch]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/addons/${deleteTarget.id}`);
      toast.success("Addon deleted");
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete addon."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4" data-testid="addons-tab">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          placeholder="Search addons..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="addons-search-input"
        />
        <Button size="sm" onClick={() => setDialogAddon(null)} data-testid="addons-new-button">
          <Plus size={15} strokeWidth={1.5} /> New Addon
        </Button>
      </div>

      <DataTable
        testId="addons-table"
        loading={state.loading}
        rows={state.rows}
        emptyTitle="No addons yet"
        columns={[
          { key: "name", header: "Name" },
          { key: "item_name", header: "Base Item", render: (r) => r.item_name || "—" },
          { key: "price", header: "Price", render: (r) => formatCurrency(r.price) },
          { key: "quantity", header: "Quantity", render: (r) => r.quantity || "—" },
          { key: "status", header: "Status", render: (r) => { const m = itemStatusMeta(r.status); return <StatusBadge label={m.label} variant={m.variant} />; } },
          {
            key: "actions", header: "", render: (r) => (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => setDialogAddon(r)} data-testid={`addons-edit-${r.id}`}>
                  <Pencil size={14} strokeWidth={1.5} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`addons-delete-${r.id}`}>
                  <Trash2 size={14} strokeWidth={1.5} className="text-danger" />
                </Button>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} onPageChange={setPage} total={state.total} pageSize={LIMIT} testId="addons-pagination" />

      <AddonFormDialog
        open={dialogAddon !== undefined} onOpenChange={(v) => !v && setDialogAddon(undefined)}
        addon={dialogAddon} onSaved={() => { setDialogAddon(undefined); refresh(); }}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this addon?" description={`"${deleteTarget?.name}" will be marked deleted.`}
        confirmLabel="Delete Addon" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="addons-delete-confirm-dialog"
      />
    </div>
  );
}
